/**
 * Phonics Spelling Game
 * Listen and spell the word
 */
import { useState, useEffect, useCallback } from 'react';
import { PhonicsRule } from '../../../data/natural-phonics';
import { getWordEmoji } from '../../../data/phonics-images';
import { scoringRules } from '../../../data/phonics-games';

interface PhonicsSpellingGameProps {
  rule: PhonicsRule;
  style: 'american' | 'british';
  onComplete: (score: number, stars: number) => void;
  onExit: () => void;
}

interface SpellingQuestion {
  word: string;
  phonetic: string;
  meaning: string;
  image: string;
  letters: string[];
}

export default function PhonicsSpellingGame({
  rule,
  style,
  onComplete,
  onExit,
}: PhonicsSpellingGameProps) {
  const [questions, setQuestions] = useState<SpellingQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [userInput, setUserInput] = useState<string[]>([]);
  const [hintLevel, setHintLevel] = useState(0);
  const [showHint, setShowHint] = useState(false);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // Generate questions
  useEffect(() => {
    const generatedQuestions = generateQuestions(rule, style);
    setQuestions(generatedQuestions);
  }, [rule, style]);

  // Reset on question change
  useEffect(() => {
    setUserInput([]);
    setHintLevel(0);
    setShowHint(false);
    setFeedback(null);
  }, [currentIndex]);

  // Speak word
  const speakWord = useCallback(() => {
    if (!currentQuestion) return;

    const utterance = new SpeechSynthesisUtterance(currentQuestion.word);
    utterance.lang = style === 'american' ? 'en-US' : 'en-GB';
    utterance.rate = 0.65; // Slow for spelling practice
    speechSynthesis.speak(utterance);
  }, [currentQuestion, style]);

  // Speak on question change
  useEffect(() => {
    if (currentQuestion && !feedback) {
      setTimeout(() => speakWord(), 300);
    }
  }, [currentQuestion, feedback, speakWord]);

  // Handle letter selection
  const handleLetterSelect = (letter: string, index: number) => {
    if (feedback) return;

    const newInput = [...userInput, letter];
    setUserInput(newInput);

    // Check if complete
    if (newInput.length === currentQuestion.word.length) {
      checkAnswer(newInput);
    }
  };

  // Check answer
  const checkAnswer = (input: string[]) => {
    const isCorrect = input.join('') === currentQuestion.word;

    if (isCorrect) {
      setFeedback('correct');
      setCorrectCount(c => c + 1);

      // Calculate points
      let points = scoringRules.spelling.correct;
      if (hintLevel === 0) {
        points += scoringRules.spelling.noHintBonus;
      }
      setScore(s => s + points);
    } else {
      setFeedback('wrong');
      // Allow retry after delay
      setTimeout(() => {
        setUserInput([]);
        setFeedback(null);
      }, 1500);
    }
  };

  // Use hint
  const useHint = () => {
    if (hintLevel >= 3 || feedback) return;

    const newHintLevel = hintLevel + 1;
    setHintLevel(newHintLevel);
    setShowHint(true);

    // Show letters progressively
    const word = currentQuestion.word;
    const newInput = [...userInput];

    if (newHintLevel === 1) {
      // Show first letter
      newInput[0] = word[0];
    } else if (newHintLevel === 2) {
      // Show first half
      for (let i = 0; i < Math.ceil(word.length / 2); i++) {
        newInput[i] = word[i];
      }
    } else if (newHintLevel === 3) {
      // Show all
      for (let i = 0; i < word.length; i++) {
        newInput[i] = word[i];
      }
    }

    setUserInput(newInput);

    // Subtract points for hint
    const penalties = Object.values(scoringRules.spelling.hintPenalty) as number[];
    const penalty = penalties[newHintLevel - 1] || 0;
    setScore(s => Math.max(0, s + penalty));

    setTimeout(() => setShowHint(false), 2000);
  };

  // Delete last letter
  const handleDelete = () => {
    if (feedback) return;
    setUserInput(input => input.slice(0, -1));
  };

  // Move to next question
  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      setShowResult(true);
    }
  };

  // Calculate stars
  const calculateStars = (accuracy: number): number => {
    if (accuracy >= 90) return 3;
    if (accuracy >= 70) return 2;
    if (accuracy >= 50) return 1;
    return 0;
  };

  // Handle game completion
  const handleComplete = () => {
    const accuracy = (correctCount / totalQuestions) * 100;
    const stars = calculateStars(accuracy);
    onComplete(score, stars);
  };

  // Show result screen
  if (showResult) {
    const accuracy = Math.round((correctCount / totalQuestions) * 100);
    const stars = calculateStars(accuracy);

    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-yellow-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center animate-bounce-in">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">拼写完成！</h2>

          {/* Stars */}
          <div className="flex justify-center gap-2 my-6">
            {[1, 2, 3].map(i => (
              <span
                key={i}
                className={`text-4xl ${i <= stars ? 'animate-star-fly' : 'opacity-30'}`}
                style={{ animationDelay: `${i * 0.2}s` }}
              >
                ⭐
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 my-6">
            <div className="bg-orange-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-orange-600">{score}</p>
              <p className="text-sm text-gray-500">得分</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-yellow-600">{correctCount}/{totalQuestions}</p>
              <p className="text-sm text-gray-500">正确</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-amber-600">{accuracy}%</p>
              <p className="text-sm text-gray-500">正确率</p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onExit}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              返回
            </button>
            <button
              onClick={handleComplete}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
            >
              确认
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-2xl text-gray-500">加载中...</div>
      </div>
    );
  }

  // Get available letters (not yet selected)
  const availableLetters = currentQuestion.letters.filter((_, idx) => {
    const usedIndices = userInput.map(l => currentQuestion.letters.indexOf(l));
    return !usedIndices.includes(idx);
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-yellow-50 p-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onExit}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span className="text-2xl">←</span>
            <span className="font-medium">退出</span>
          </button>

          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-soft">
              <span className="text-lg font-bold text-orange-600">{score}</span>
              <span className="text-sm text-gray-500 ml-1">分</span>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>第 {currentIndex + 1} 题</span>
            <span>共 {totalQuestions} 题</span>
          </div>
          <div className="h-3 bg-white rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-yellow-400 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-6">
          {/* Image and meaning */}
          <div className="text-center mb-6">
            <span className="text-6xl">{currentQuestion.image}</span>
            <p className="text-xl text-gray-600 mt-2">{currentQuestion.meaning}</p>
            <button
              onClick={speakWord}
              className="mt-4 text-3xl hover:scale-110 transition-transform"
            >
              🔊
            </button>
          </div>

          {/* Answer boxes */}
          <div className="flex justify-center gap-2 mb-8">
            {currentQuestion.word.split('').map((letter, idx) => (
              <div
                key={idx}
                className={`w-12 h-12 border-2 rounded-xl flex items-center justify-center text-xl font-bold ${
                  userInput[idx]
                    ? 'bg-orange-100 border-orange-400'
                    : 'bg-gray-50 border-gray-200'
                } ${feedback === 'correct' && userInput[idx] === letter ? 'animate-correct-flash' : ''}`}
              >
                {userInput[idx] || ''}
              </div>
            ))}
          </div>

          {/* Letter selection */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {currentQuestion.letters.map((letter, idx) => {
              const isUsed = userInput.includes(letter) &&
                userInput.filter(l => l === letter).length >
                currentQuestion.letters.slice(0, idx).filter(l => l === letter).length;

              return (
                <button
                  key={idx}
                  onClick={() => handleLetterSelect(letter, idx)}
                  disabled={isUsed || !!feedback || userInput.length >= currentQuestion.word.length}
                  className={`w-14 h-14 rounded-xl text-xl font-bold transition-all ${
                    isUsed
                      ? 'bg-gray-100 text-gray-300'
                      : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:scale-105 active:scale-95'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>

          {/* Control buttons */}
          <div className="flex justify-center gap-4">
            <button
              onClick={handleDelete}
              disabled={userInput.length === 0 || !!feedback}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              ← 删除
            </button>
            <button
              onClick={useHint}
              disabled={hintLevel >= 3 || !!feedback}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                hintLevel >= 3
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              💡 提示 ({3 - hintLevel}次)
            </button>
          </div>

          {/* Hint penalty notification */}
          {showHint && hintLevel > 0 && (
            <p className="text-center mt-4 text-yellow-600 text-sm animate-slide-up">
              使用提示 -{(Object.values(scoringRules.spelling.hintPenalty) as number[])[hintLevel - 1]}分
            </p>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`bg-white rounded-2xl shadow-lg p-6 text-center animate-slide-up ${
              feedback === 'correct' ? 'border-2 border-green-400' : 'border-2 border-red-400'
            }`}
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl">{feedback === 'correct' ? '✅ 正确！' : '❌ 错误'}</span>
            </div>
            {feedback === 'correct' && (
              <>
                <p className="mt-2 text-2xl font-bold text-gray-800">{currentQuestion.word}</p>
                <p className="text-gray-500">{currentQuestion.phonetic}</p>
                {hintLevel === 0 && (
                  <p className="mt-2 text-green-500 font-bold">
                    🌟 不使用提示 +{scoringRules.spelling.noHintBonus}分!
                  </p>
                )}
                <button
                  onClick={handleNext}
                  className="mt-4 px-8 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                >
                  下一题 →
                </button>
              </>
            )}
            {feedback === 'wrong' && (
              <p className="mt-2 text-gray-600">再试一次...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Generate spelling questions
function generateQuestions(rule: PhonicsRule, _style: 'american' | 'british'): SpellingQuestion[] {
  const questions: SpellingQuestion[] = [];

  for (const example of rule.examples) {
    // Create shuffled letters
    const letters = example.word.split('');

    // Add some distractor letters
    const distractors = getDistractorLetters(example.word, 2);
    letters.push(...distractors);

    // Shuffle
    const shuffled = letters.sort(() => Math.random() - 0.5);

    questions.push({
      word: example.word,
      phonetic: _style === 'american' ? example.americanPhonetic : example.britishPhonetic,
      meaning: example.meaning,
      image: getWordEmoji(example.word),
      letters: shuffled,
    });
  }

  return questions;
}

// Get distractor letters for spelling
function getDistractorLetters(word: string, count: number): string[] {
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const consonants = ['b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'r', 's', 't', 'w'];

  const distractors: string[] = [];
  const wordLetters = new Set(word.toLowerCase().split(''));

  // Add vowels not in word
  const availableVowels = vowels.filter(v => !wordLetters.has(v));
  for (let i = 0; i < Math.min(count, availableVowels.length); i++) {
    const randomIdx = Math.floor(Math.random() * availableVowels.length);
    distractors.push(availableVowels[randomIdx]);
    availableVowels.splice(randomIdx, 1);
  }

  // Add consonants not in word
  while (distractors.length < count) {
    const randomIdx = Math.floor(Math.random() * consonants.length);
    const letter = consonants[randomIdx];
    if (!wordLetters.has(letter) && !distractors.includes(letter)) {
      distractors.push(letter);
    }
  }

  return distractors;
}
